import type { AddBookingPayload } from '../../infrastructure/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { tokPx } from '@sunwebgroup/ui-components'
import { Button, DateField, IndeterminateProgressCircle, TextField, Typography } from '@sunwebgroup/ui-components/uds'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'
import { getBookingDateRange, validateDateInRange } from '../../domain/dateValidation'
import bookingsOverviewUseCases from '../../domain/useCases'
import { useBookingsOverviewStore } from '../../infrastructure/useBookingsOverviewStore'
import { getAddBookingErrorKeyMessage, isAddBookingError, isAddBookingLoading } from '../selectors'

const Form = styled.form<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_16')};
  margin-top: ${tokPx('primitives_space_16')};
  flex-grow: 1;

    ${({ $disabled }) =>
      $disabled
      && css`
      opacity: 0.25;
      pointer-events: none;
    `}
`

const FormButton = styled(Button)`
  margin-top: auto;
`

const FormWrapper = styled.div`
  position: relative;
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const BannerStyled = styled(Banner)`
  margin: ${tokPx('primitives_space_8')} 0;
`

interface AddBookingProps {
  contextItemId: string
}

export function AddBooking({
  contextItemId,
}: AddBookingProps) {
  const intl = useIntl()
  const { register, handleSubmit, formState: { errors } } = useForm<AddBookingPayload>()
  const { minDate, maxDate } = getBookingDateRange()
  const isLoading = useBookingsOverviewStore(isAddBookingLoading)
  const isError = useBookingsOverviewStore(isAddBookingError)
  const errorMessage = useBookingsOverviewStore(getAddBookingErrorKeyMessage)

  const validateDepartureDate = (value: string) => {
    const result = validateDateInRange(value, { minDate, maxDate })
    switch (result) {
      case 'missing':
        return intl.formatMessage({ id: 'departure_date_required' })
      case 'tooEarly':
        return intl.formatMessage({ id: 'departure_date_too_early' })
      case 'tooLate':
        return intl.formatMessage({ id: 'departure_date_too_late' })
      default:
        return true
    }
  }

  const onSubmit = (data: AddBookingPayload) => {
    bookingsOverviewUseCases.addBooking(data, contextItemId)
  }
  return (
    <>
      {isError && (
        <BannerStyled
          color="warning"
          size="S"
          hasIcon
          contentIsCentered
        >
          <Banner.Text>{intl.formatMessage({ id: errorMessage })}</Banner.Text>
        </BannerStyled>
      )}
      <Typography variant="body-m-regular-snug">
        {intl.formatMessage({ id: 'bookings_overview_add_booking_description' })}
      </Typography>
      <FormWrapper>
        <Form onSubmit={handleSubmit(onSubmit)} $disabled={isLoading}>
          <TextField
            label={intl.formatMessage({ id: 'last_name_label' })}
            helpText={!errors.lastName ? intl.formatMessage({ id: 'bookings_overview_add_booking_last_name_help' }) : undefined}
            feedbackText={errors.lastName?.message}
            status={errors.lastName ? 'error' : undefined}
            {...register('lastName', {
              required: intl.formatMessage({ id: 'last_name_required' }),
              minLength: {
                value: 2,
                message: intl.formatMessage({ id: 'last_name_min_length' }),
              },
              maxLength: { value: 60, message: intl.formatMessage({ id: 'last_name_max_length' }) },
              pattern: {
                value: /^[a-zA-Zèéêëēėęÿûüùúūîïíīįìôöòóœøōõàáâäæãåāßśšłžźżçćčñń\s-]+$/,
                message: intl.formatMessage({ id: 'first_name_invalid_chars' }),
              },
            })}
          />
          <TextField
            label={intl.formatMessage({ id: 'booking_number_label' }, { bookingNumber: '' })}
            feedbackText={errors.bookingNumber?.message}
            status={errors.bookingNumber ? 'error' : undefined}
            {...register('bookingNumber', {
              required: intl.formatMessage({ id: 'booking_number_required' }),
              pattern: {
                value: /^\d{7}$/,
                message: intl.formatMessage({ id: 'booking_number_invalid' }),
              },
            })}
          />
          <DateField
            label={intl.formatMessage({ id: 'departure_date_label' })}
            feedbackText={errors.departureDate?.message}
            status={errors.departureDate ? 'error' : undefined}
            minDate={minDate}
            maxDate={maxDate}
            {...register('departureDate', {
              required: intl.formatMessage({ id: 'departure_date_required' }),
              validate: validateDepartureDate,
            })}
          />
          <FormButton
            type="submit"
            fullWidth
            variant="primary"
          >
            {intl.formatMessage({ id: 'bookings_overview_add_booking' })}
          </FormButton>
        </Form>
        {isLoading && (
          <LoaderOverlay>
            <IndeterminateProgressCircle />
          </LoaderOverlay>
        )}
      </FormWrapper>
    </>
  )
}
